import React from "react";

import R from "ramda";

import { format } from "date-fns";

import { withProps, componentFromProp } from "recompose";

import { Image, Box, Flex, Markup, Text, Link } from "src/components/base";

import HighlightedPhoto from "src/components/HighlightedPhoto";

import { shevy, themeColor } from "src/styles";

import LearnMoreLink from "src/components/LearnMoreLink";

const formatPublicationDate = date => format(date, "MMMM D, YYYY");

const Title = withProps(({ style = {} }) => ({
  fontWeight: "bold",
  textTransform: "uppercase",
  textDecoration: "none",
  style: { ...shevy.h3, ...style },
}))(Text);

const Alert = ({ content, summary, theme, title, url }) => (
  <Box>
    <Title color={themeColor(theme)} style={{ marginBottom: 0 }}>
      {title}
    </Title>
    <Flex
      flexDirection={["column", "column", "row"]}
      justify="space-between"
      align={["stretch", "stretch", "center"]}
    >
      <Markup html={summary} mr={4} />
      {Boolean(content) && (
        <LearnMoreLink
          to={url}
          alignSelf={["flex-end", "flex-end", "center"]}
          title={title}
        />
      )}
    </Flex>
  </Box>
);

const Archive = ({ content, publicationDate, summary, theme, title, url }) => (
  <Box>
    <Link
      to={url}
      textDecoration="none"
      hover={{
        textDecoration: "underline",
      }}
    >
      <Title color={themeColor(theme)} style={{ marginBottom: 0 }}>
        {title}
      </Title>
    </Link>
    <Text color="muted">{formatPublicationDate(publicationDate)}</Text>
    <Flex
      flexDirection={["column", "column", "row"]}
      justify="space-between"
      align={["stretch", "stretch", "center"]}
    >
      <Markup html={summary} mr={4} />
    </Flex>
  </Box>
);

const DidYouKnow = ({ summary, theme, title, url }) => (
  <Box mx={[3, 3, 4]} my={[3, 3, 4]}>
    <Text textTransform="uppercase" mb={2} fontWeight="semibold">
      Did You Know
    </Text>
    <Title color={themeColor(theme)} to={url}>
      {title}
    </Title>
    <Flex
      flexDirection={["column", "column", "row"]}
      justify="space-between"
      align={["stretch", "stretch", "center"]}
    >
      <Markup html={summary} mr={4} style={{ width: "100%" }} />
      <LearnMoreLink
        to={url}
        alignSelf={["flex-end", "flex-end", "center"]}
        title={title}
      />
    </Flex>
  </Box>
);

const PhotoWithOverlay = ({ image, summary, theme, title, url }) => (
  <Flex
    mx={0}
    my={[3, 3, 4]}
    align={[undefined, undefined, "center"]}
    flexDirection={["column", "column", "row"]}
  >
    <HighlightedPhoto
      image={image}
      mr={[3, 3, -5]}
      flex={[undefined, undefined, 1]}
    />
    <Flex
      flex={[undefined, undefined, 1]}
      ml={[3, 3, -5]}
      flexDirection="column"
      align="flex-end"
    >
      <Box
        bg={themeColor(theme, true)}
        px={3}
        pt={[6, 6, 3]}
        pb={3}
        mt={[-5, -5, 4]}
        borderRadius={4}
        width={1}
        style={{
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          zIndex: 2,
        }}
      >
        <Title color="white" to={url} flex={1}>
          {title}
        </Title>
        <Box pl={[0, 0, 4]}>
          <Markup html={summary} color="white" linkColor="white" />
        </Box>
      </Box>
      <Box mt={3} mr={[3, 3, 4]}>
        <LearnMoreLink to={url} title={title} />
      </Box>
    </Flex>
  </Flex>
);

const StandalonePhoto = ({ image, summary, theme, title, url }) => (
  <Flex
    mx={[3, 3, 4]}
    mt={4}
    align="stretch"
    justify="flex-start"
    flexDirection={["column-reverse", "column-reverse", "row"]}
  >
    <Flex
      flex={[undefined, undefined, 1]}
      justify="center"
      align="flex-start"
      px={[3, 3, 4]}
    >
      {image && (
        <Image
          resolutions={image.resolutions}
          fadeIn={true}
          alt={image.description || image.title}
          style={{ display: "block" }}
        />
      )}
    </Flex>
    <Flex
      flex={[undefined, undefined, 1]}
      flexDirection="column"
      align="flex-end"
      mb={4}
    >
      <Flex justify="center" flexDirection="column" width={1}>
        <Title color={themeColor(theme)} to={url}>
          {title}
        </Title>
        <Markup html={summary} mb={[0, 0, -4]} />
      </Flex>
      <Box mt={[0, 0, 4]}>
        <LearnMoreLink to={url} title={title} />
      </Box>
    </Flex>
  </Flex>
);

const styleComponents = {
  didyouknow: DidYouKnow,
  photowithoverlay: PhotoWithOverlay,
  standalonephoto: StandalonePhoto,
  alert: Alert,
  archive: Archive, // Use Alert style for now
};

const Embed = withProps(props =>
  R.pipe(
    R.propOr("DidYouKnow", "embedStyle"),
    R.replace(/\s/g, ""),
    R.toLower,
    R.propOr(DidYouKnow, R.__, styleComponents),
    component => ({
      component,
      summary: R.path(["childMarkdownRemark", "html"], props.summary),
      title: props.embedTitle || props.title,
    })
  )(props)
)(componentFromProp("component"));

export default Embed;

export const contentFragment = graphql`
  fragment ArticleEmbed on ContentfulArticle {
    id
    __typename
    theme
    title
    embedStyle
    url
    content {
      id
    }
    summary {
      childMarkdownRemark {
        html
      }
    }
    publicationDate
    unpublishDate
    timestamp: updatedAt(formatString: "x")
    image {
      title
      description
      ...HighlightedPhoto
      resolutions(width: 300, quality: 80) {
        ...GatsbyContentfulResolutions_withWebp
      }
    }
  }

  fragment PageEmbed on ContentfulPage {
    id
    __typename
    theme
    title
    embedTitle
    embedStyle
    url
    summary: embedSummary {
      childMarkdownRemark {
        html
      }
    }
    image: embedImage {
      title
      description
      ...HighlightedPhoto
      resolutions(width: 300, quality: 80) {
        ...GatsbyContentfulResolutions_withWebp
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/Embed/Embed.js